import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { VerticalCatalogEntry } from '../lib/verticals';

/**
 * The API's vertical registry (GET /verticals/catalog — the same rows as
 * GET /verticals, under `verticals` rather than `data`). One query, one key:
 * useActiveVertical and every label lookup share it, so the registry is
 * fetched once per session.
 */
export function useVerticalCatalog() {
  return useQuery<VerticalCatalogEntry[]>({
    queryKey: ['verticals', 'catalog'],
    queryFn: () =>
      api.verticals.catalog().then(r => {
        const rows = r.data?.verticals;
        if (!Array.isArray(rows)) throw new Error('vertical catalog: no verticals array in response');
        return rows;
      }),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}

/**
 * Resolves a vertical key to the registry's label. A key the registry does not
 * name — or any key while the registry has not arrived — shows AS SENT: the
 * row does have a vertical, so an em dash would claim it has none, and a
 * guessed label would claim to know which. Pinned by
 * PayrollSummaryScreen.clock-rounding.test.tsx ("an unknown key shows as sent").
 * An absent key is the caller's to render as `—`.
 */
export function useVerticalLabel(): (key: string) => string {
  const { data } = useVerticalCatalog();
  return useCallback(
    (key: string) => data?.find(v => v.key === key)?.label ?? key,
    [data],
  );
}
